import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchCaseTwins,
  fetchFirmDuplicates,
  dismissDuplicate,
  fetchMergePreview,
  mergeCases,
  type CaseTwin,
  type FirmDuplicatePair,
  type FirmDuplicates,
} from "../api/duplicates";

const twinsKey = (caseId: string) => ["duplicates", "case", caseId];
const FIRM_KEY = ["duplicates", "firm"];

export function useFirmDuplicates(limit: number) {
  return useQuery({
    queryKey: [...FIRM_KEY, limit],
    queryFn: () => fetchFirmDuplicates(limit),
    staleTime: 60_000,
    retry: false,
    // "Show all" widens the page; the rows on screen stay while the wider one loads.
    placeholderData: keepPreviousData,
  });
}

const samePair = (p: FirmDuplicatePair, [a, b]: [string, string]) =>
  p.case1.caseId === a && p.case2.caseId === b;

/** The pair stops being reported everywhere: the firm list and both matter cards. */
export function useDismissPair() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ([caseId1, caseId2]: [string, string]) => dismissDuplicate(caseId1, caseId2),
    // The list is a page with a printed total: the row goes now, the total
    // moves with it, and the refetch refills the page.
    onMutate: async (ids: [string, string]) => {
      await queryClient.cancelQueries({ queryKey: FIRM_KEY });
      const previous = queryClient.getQueriesData<FirmDuplicates>({ queryKey: FIRM_KEY });
      queryClient.setQueriesData<FirmDuplicates>({ queryKey: FIRM_KEY }, (old) => {
        if (!old) return old;
        const groups = old.groups.filter((p) => !samePair(p, ids));
        return { ...old, groups, total: old.total - (old.groups.length - groups.length) };
      });
      return { previous };
    },
    onError: (_err, _ids, context) => {
      for (const [key, data] of context?.previous ?? []) queryClient.setQueryData(key, data);
    },
    // Not awaited: the buttons and the error line follow the server's answer,
    // not the re-scan behind the refetch.
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: FIRM_KEY });
      void queryClient.invalidateQueries({ queryKey: ["duplicates", "case"] });
    },
  });
}

export function useCaseTwins(caseId: string) {
  return useQuery({
    queryKey: twinsKey(caseId),
    queryFn: () => fetchCaseTwins(caseId),
    staleTime: 60_000,
  });
}

export function useDismissTwin(caseId: string) {
  const queryClient = useQueryClient();
  const key = twinsKey(caseId);
  return useMutation({
    mutationFn: (twinCaseId: string) => dismissDuplicate(caseId, twinCaseId),
    onMutate: async (twinCaseId: string) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<CaseTwin[]>(key);
      queryClient.setQueryData<CaseTwin[]>(key, (old) =>
        (old ?? []).filter((t) => t.caseId !== twinCaseId),
      );
      return { previous };
    },
    onError: (_err, _twinCaseId, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: key }),
  });
}

/** Read only while the dialog is open: the hook lives in a parent the closed dialog leaves mounted. */
export function useMergePreview(keepCaseId: string, mergeCaseId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["duplicates", "preview", keepCaseId, mergeCaseId],
    queryFn: () => fetchMergePreview(keepCaseId, mergeCaseId),
    enabled,
    // The token is the decision: a preview is never served from memory.
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
}

export function useMergeCases() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: mergeCases,
    // The list and both matters are served from memory for a minute. The
    // twins and firm queries are re-read; the preview just consumed is not.
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["duplicates", "case"] }),
        queryClient.invalidateQueries({ queryKey: FIRM_KEY }),
        queryClient.invalidateQueries({ queryKey: ["matter"] }),
        queryClient.invalidateQueries({ queryKey: ["matters"] }),
      ]);
    },
  });
}
